import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserCheck, Plus, Send, Copy, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STRENGTH_STYLE = {
  Strong: "bg-teal text-white border-0",
  Moderate: "bg-amber-500 text-white border-0",
  Weak: "bg-red-500 text-white border-0",
};

const STATUS_STYLE = {
  DRAFT: "bg-neutral-200 text-neutral-700 border-0",
  SENT: "bg-amber-100 text-amber-800 border-0",
  SUBMITTED: "bg-teal text-white border-0",
};

export default function ReferenceChecksPanel({ candidateId, candidateName }) {
  const [refs, setRefs] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ referee_name: "", referee_email: "", relationship: "Manager" });
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/recruit/candidates/${candidateId}/references`).then(r => setRefs(r.data)).catch(() => {});
  useEffect(() => { if (candidateId) load(); }, [candidateId]);

  const create = async () => {
    if (!form.referee_name || !form.referee_email) { toast.error("Name and email required"); return; }
    setBusy(true);
    try {
      await api.post(`/recruit/candidates/${candidateId}/references`, form);
      toast.success("Reference added — send when ready");
      setForm({ referee_name: "", referee_email: "", relationship: "Manager" });
      setAddOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const send = async (ref) => {
    try {
      const { data } = await api.post(`/recruit/references/${ref.reference_id}/send`);
      const url = `${window.location.origin}${data.public_link}`;
      await navigator.clipboard.writeText(url);
      if (data.email_mocked === false) {
        toast.success(`Emailed to ${ref.referee_email} · link also copied`);
      } else {
        toast.success("Link copied — connect an inbox in Settings to email automatically");
      }
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const copyLink = async (ref) => {
    const url = `${window.location.origin}/reference/${ref.token}`;
    await navigator.clipboard.writeText(url);
    toast.success("Reference link copied");
  };

  const del = async (ref) => {
    if (!confirm(`Remove reference from ${ref.referee_name}?`)) return;
    await api.delete(`/recruit/references/${ref.reference_id}`);
    load();
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="references-panel">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-pink" />
            <h3 className="font-semibold">Reference checks</h3>
            <Badge variant="outline" className="text-[10px]">{refs.length}</Badge>
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAddOpen(true)} data-testid="add-reference-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add reference
          </Button>
        </div>

        {refs.length === 0 ? (
          <div className="text-neutral-500 text-sm py-6 text-center">
            No references yet. Add a former manager or peer to email them a short form.
          </div>
        ) : (
          <div className="space-y-2">
            {refs.map(r => {
              const isOpen = expandedId === r.reference_id;
              return (
                <div key={r.reference_id} className="rounded-lg border" data-testid={`ref-row-${r.reference_id}`}>
                  <button onClick={() => setExpandedId(isOpen ? null : r.reference_id)}
                    className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-neutral-50 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-navy text-white grid place-items-center text-xs font-bold shrink-0">
                        {r.referee_name.split(" ").map(x => x[0]).join("").slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{r.referee_name} <span className="text-neutral-400 font-normal">· {r.relationship}</span></div>
                        <div className="text-xs text-neutral-500 truncate">{r.referee_email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.ai_strength && <Badge className={STRENGTH_STYLE[r.ai_strength]}>{r.ai_strength}</Badge>}
                      <Badge className={STATUS_STYLE[r.status]}>{r.status}</Badge>
                      {isOpen ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t px-4 py-3 space-y-3 bg-neutral-50/50" data-testid={`ref-expand-${r.reference_id}`}>
                      <div className="flex flex-wrap gap-2">
                        {r.status === "DRAFT" && (
                          <Button size="sm" onClick={() => send(r)} className="btn-primary rounded-full" data-testid={`ref-send-${r.reference_id}`}>
                            <Send className="w-3.5 h-3.5 mr-1" /> Send (copy link)
                          </Button>
                        )}
                        {r.status !== "SUBMITTED" && (
                          <Button size="sm" variant="outline" onClick={() => copyLink(r)} data-testid={`ref-copy-link-${r.reference_id}`}>
                            <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
                          </Button>
                        )}
                        {r.status !== "SUBMITTED" && (
                          <Button size="sm" variant="ghost" onClick={() => del(r)} className="text-red-500 hover:text-red-600" data-testid={`ref-delete-${r.reference_id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                          </Button>
                        )}
                      </div>

                      {r.status === "SUBMITTED" && r.ai_summary && (
                        <div className="text-sm space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-widest text-neutral-500">Alfred's summary</div>
                            {r.one_tap && (
                              <Badge className="bg-pink/10 text-pink border border-pink/30 text-[9px] uppercase tracking-widest" data-testid={`ref-one-tap-badge-${r.reference_id}`}>
                                One-tap · {r.one_tap_verdict}
                              </Badge>
                            )}
                          </div>
                          <p className="text-neutral-800">{r.ai_summary.summary}</p>
                          {r.ai_summary.strengths && (
                            <div>
                              <div className="text-xs font-semibold text-neutral-600 mt-2 mb-1">Strengths</div>
                              <ul className="text-xs text-neutral-700 list-disc pl-5 space-y-0.5">
                                {r.ai_summary.strengths.map((s, i) => <li key={i}>{s}</li>)}
                              </ul>
                            </div>
                          )}
                          {r.ai_summary.growth_area && (
                            <div>
                              <div className="text-xs font-semibold text-neutral-600 mt-2 mb-1">Growth area</div>
                              <p className="text-xs text-neutral-700">{r.ai_summary.growth_area}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {r.status === "SENT" && (
                        <div className="text-xs text-neutral-500 italic space-y-1" data-testid={`ref-waiting-${r.reference_id}`}>
                          <div>Waiting for {r.referee_name} to submit the form.</div>
                          {r.nudge_count > 0 ? (
                            <div className="not-italic text-[11px] text-pink flex items-center gap-1">
                              <Send className="w-3 h-3" /> Auto-nudged {r.nudge_count}/3
                              {r.last_nudged_at && <> · last at {new Date(r.last_nudged_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</>}
                            </div>
                          ) : (
                            <div className="not-italic text-[11px] text-neutral-400">Alfred will auto-nudge in ~48h if no reply.</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add a reference for {candidateName}</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Alfred will email a short 6-question form and summarize the answers for the hiring team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Referee name</Label>
              <Input value={form.referee_name} onChange={(e) => setForm({ ...form, referee_name: e.target.value })}
                className="mt-1.5" data-testid="ref-name-input" placeholder="e.g. Jane Doe" />
            </div>
            <div>
              <Label>Referee email</Label>
              <Input type="email" value={form.referee_email} onChange={(e) => setForm({ ...form, referee_email: e.target.value })}
                className="mt-1.5" data-testid="ref-email-input" placeholder="jane@example.com" />
            </div>
            <div>
              <Label>Relationship</Label>
              <Select value={form.relationship} onValueChange={(v) => setForm({ ...form, relationship: v })}>
                <SelectTrigger className="mt-1.5" data-testid="ref-relationship"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Manager">Manager / supervisor</SelectItem>
                  <SelectItem value="Peer">Peer</SelectItem>
                  <SelectItem value="Direct report">Direct report</SelectItem>
                  <SelectItem value="Client">Client / customer</SelectItem>
                  <SelectItem value="Mentor">Mentor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={create} disabled={busy} data-testid="ref-create-btn">
              {busy ? "Adding…" : "Add reference"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
